// THE Assembly panel — the one surface on which a customer says "pack this into
// kits" and authors what goes in them. There is one assembly flow: an `assembly`
// item, one of the product grid's tiles, holding its kit at
// QuoteLine.config.assembly (ADR-020). This panel is mounted inside that item's
// editor, once per item, bound to that item's own snapshot. `kit_contents` is an
// array with add / remove / reorder, and a second mount over the SAME array would
// be two "Add row" affordances, two reorder controls racing each other, and two
// per-row uploaders registering into the same Asset upsert (ADR-018 Decision 2 as
// restated by ADR-019 Decision 1) — one mount per item, each over its own array,
// keeps that structural.
//
// It asks no kit count. How many kits is the item's own Quantity field (ADR-019),
// one section up in the same editor.
//
// A submitted order still renders it, `disabled`: every control then reads the
// customer's answer back as static text rather than a greyed-out input, because
// the kit's contents and build recipe live nowhere else on the page — the spec
// rail beside the item recaps the kit, it does not list it.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  assemblyMode,
  scopeSchema,
  type AssemblySnapshot,
  type FieldValues,
  type FormSchemaDto,
} from 'shared';

import { AssemblyDetailFields } from './AssemblyDetailFields.js';
import { CollapsedRowsScope } from './CollapsibleRows.js';
import { FieldIdPrefixContext, fieldDomId } from './fieldDomId.js';
import { readBackValue } from './FieldReadBack.js';
import type { KitContentLineOption } from './KitContentsEditor.js';
import { KittingFields, NumberField } from './KittingFields.js';
import { scrubConfigForVisibility } from './SchemaForm.js';
import type { AssemblyPhotos } from './useAssemblyPhotos.js';

type AssemblyPanelProps = {
  quote_id: string | undefined;
  // This item's reference photos — the finished-kit shot and every step's. The
  // item editor owns the hook, not this panel, because the spec rail beside the
  // panel wears the finished-kit photo too, and two mounts would be two fetches
  // whose uploads never reach each other.
  photos: AssemblyPhotos;
  // The active form schema. Its quote-scope groups are the packing-detail axes,
  // rendered below by the same SchemaForm the projects use, bound to this kit.
  schema: FormSchemaDto;
  // The item's kit — its config.assembly, `{}` for an item not yet given one.
  value: AssemblySnapshot;
  onChange: (next: AssemblySnapshot) => void;
  lines: readonly KitContentLineOption[];
  hasUnsavedLines?: boolean;
  // Adding a contents row asks the caller to persist the quote as it stands. The
  // kit's rows are snapshot state a reload otherwise drops — see
  // KitContentsEditor's own prop for what the caller's save has to preserve.
  onRequestSave?: (() => void) | undefined;
  errors: Record<string, string>;
  disabled?: boolean;
  // How deep this panel sits in the page's heading outline. Threaded rather than
  // fixed per component because the two lists and the packing-detail axes must
  // agree on ONE level, and the item editor that mounts it owns the outline.
  headingLevel: 'h4' | 'h5';
  // Namespaces every DOM id this panel renders by the item's local id: a quote
  // may hold several assembly items, and a bare `assembly.kit_type` would be a
  // duplicate id — the wrong control focused, the wrong label bound, two radio
  // groups sharing one name. `errors` stay keyed by BARE field key; see
  // fieldDomId.
  fieldIdPrefix: string;
};

// Everything the customer authors about an assembly item's kit.
export function AssemblyPanel({
  quote_id,
  photos,
  schema,
  value,
  onChange,
  lines,
  hasUnsavedLines = false,
  onRequestSave,
  errors,
  disabled,
  headingLevel,
  fieldIdPrefix,
}: AssemblyPanelProps): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const assembly: AssemblySnapshot = value;
  const isRework = assemblyMode(assembly) === 'rework';
  const isPerUnit = assemblyMode(assembly) === 'per_unit';

  // The quote-scope half of the schema — the packing-detail axes. The scrub
  // below needs it as much as the section does, and ONE narrowing is what keeps
  // the renderer and the scrub agreeing about which fields exist. scopeSchema
  // returns the same object when nothing is filtered out.
  const detailSchema = useMemo(() => scopeSchema(schema, 'quote'), [schema]);
  // Resolved here rather than through the hook: this component OWNS the prefix
  // (it is what provides the context below), so reading it back from context
  // would be reading its own prop the long way round.
  const instructionsDomId = fieldDomId(fieldIdPrefix, 'assembly.assembly_instructions');

  // EVERY assembly write lands here, from the hand-written fields as well as the
  // schema-driven ones, and is scrubbed of values whose gating question no longer
  // shows them. That matters because the gate can live on the OTHER side: box
  // finish is asked only for a boxed kit, and kit_type is edited in KittingFields
  // where SchemaForm's own scrub never runs.
  function writeAssembly(next: AssemblySnapshot): void {
    const scrubbed = scrubConfigForVisibility(
      detailSchema,
      next as FieldValues,
      assembly as FieldValues,
    ) as AssemblySnapshot;
    onChange(scrubbed);
  }

  function patchAssembly(patch: Partial<AssemblySnapshot>): void {
    writeAssembly({ ...assembly, ...patch });
  }

  const fields = (
    <div className="assembly-layout__fields">
      <KittingFields
        assembly={assembly}
        onPatchAssembly={patchAssembly}
        errors={errors}
        disabled={disabled}
        lines={lines}
        hasUnsavedLines={hasUnsavedLines}
        onRequestSave={onRequestSave}
        photos={photos}
        headingLevel={headingLevel}
      />
      <AssemblyDetailFields
        schema={detailSchema}
        assembly={assembly}
        onChange={writeAssembly}
        errors={errors}
        disabled={disabled}
        headingLevel={headingLevel}
      />
      {/* These three are authored here rather than inside KittingFields so each
      lands beside what it is about: the labels flag next to label_purpose /
      label_placement, the per-pack count next to overboxing, and free text
      last of all (§4.15). */}
      {/* A rework is never OFFERED this: "apply labels you supply" is one of the
      rework ACTIONS, so asking again offers the same job twice (the
      2026-08-20 meeting). But a value already set — a kit build the
      customer then switched to rework — still transmits, because nothing
      captured is dropped (ADR-018 Decision 9, pinned by rework-mode.test.ts).
      Hiding the box outright would leave that flag riding to Nexus with no
      way to see or clear it, so in rework mode it renders only when it is
      already ticked: never a new question, always a correctable one. */}
      {/* On a submitted order it reads back as a STATEMENT rather than a ticked
      box: a checkbox carries its answer in the checked state, and a natively
      disabled input is out of the tab order, so the flag was unreachable by
      keyboard and screen reader alike — the same defect the packing axes
      above had (see AssemblyDetailFields). Rendered only when it is true,
      because an unticked box is not an answer to read back. */}
      {disabled
        ? assembly.purchased_labels === true && (
            <p className="value--locked">
              {t('orderServices.assembly.purchasedLabelsCheckbox')}
            </p>
          )
        : (!isRework || assembly.purchased_labels === true) && (
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={assembly.purchased_labels === true}
                onChange={(e) => patchAssembly({ purchased_labels: e.target.checked })}
              />
              <span>
                {t('orderServices.assembly.purchasedLabelsCheckbox')}
                <span className="help help--block">
                  {isRework
                    ? t('orderServices.assembly.rework.purchasedLabelsCarriedHelp')
                    : isPerUnit
                      ? t('orderServices.assembly.perUnit.purchasedLabelsHelp')
                      : t('orderServices.assembly.purchasedLabelsHelp')}
                </span>
              </span>
            </label>
          )}
      <NumberField
        fieldKey="assembly.units_per_pack"
        label={
          isRework
            ? t('orderServices.assembly.rework.unitsPerPackLabel')
            : isPerUnit
              ? t('orderServices.assembly.perUnit.unitsPerPackLabel')
              : t('orderServices.assembly.unitsPerPackLabel')
        }
        placeholder={t('orderServices.assembly.unitsPerPackPlaceholder')}
        value={assembly.units_per_pack}
        onChange={(v) => patchAssembly({ units_per_pack: v })}
        errors={errors}
        disabled={disabled}
        required={false}
        compact
      />
      <div className="form-row">
        <label htmlFor={instructionsDomId}>
          {isRework
            ? t('orderServices.assembly.rework.instructionsLabel')
            : t('orderServices.assembly.instructionsLabel')}
          {/* The canonical optional marker — the same span ShipToForm puts after
          every optional label. It carries the fact on its own: the help
          below describes what to write, not whether to. */}
          <span className="help"> {t('shipTo.fields.optionalSuffix')}</span>
        </label>
        {/* The help lists what could go in the box. With the customer's own text
        standing in the box instead of an invitation to write it, the list is
        asking for something this surface cannot take. */}
        {!disabled && (
          <span className="help help--block">
            {isRework
              ? t('orderServices.assembly.rework.instructionsHelp')
              : t('orderServices.assembly.instructionsHelp')}
          </span>
        )}
        {/* `readOnly` rather than `disabled` on a submitted order, with the locked
        styling that keeps it from looking like a box still open for writing —
        the same pairing the build steps' instruction box takes. */}
        <textarea
          id={instructionsDomId}
          rows={3}
          value={
            disabled
              ? readBackValue(assembly.assembly_instructions)
              : (assembly.assembly_instructions ?? '')
          }
          onChange={(e) => patchAssembly({ assembly_instructions: e.target.value })}
          {...(disabled
            ? { readOnly: true, 'aria-readonly': true, className: 'input--locked' }
            : {})}
        />
      </div>
    </div>
  );

  return (
    <FieldIdPrefixContext.Provider value={fieldIdPrefix}>
      {/* The quote these rows belong to. Row folds live outside the components
          that render them, so a remount does not spring a long list back open —
          and the scope is what stops a CLONE inheriting them, since a clone
          copies every `row_id` and `step_id` through verbatim. */}
      <CollapsedRowsScope scope={quote_id ?? ''}>
        {fields}
      </CollapsedRowsScope>
    </FieldIdPrefixContext.Provider>
  );
}
